import type {
    DocsThemeName
} from "../../theme";

export type AnatomyRect = {
    height: number;
    width: number;
    x: number;
    y: number;
};

export type AnatomyPartKind = "shape" | "media" | "icon" | "text";

export type AnatomyInsets = {
    bottom: number;
    right: number;
    left: number;
    top: number;
};

export type AnatomyIconStroke = {
    linejoin: "miter" | "round" | "bevel";
    linecap: "square" | "round" | "butt";
    width: number;
};

export type AnatomyCornerRadii = [number, number, number, number];

export type AnatomyPart = {
    iconStroke: AnatomyIconStroke | null;
    textLines: Array<AnatomyRect>;
    borderInsets: AnatomyInsets;
    radii?: AnatomyCornerRadii;
    iconViewBox: string | null;
    borderColor: string | null;
    fontFamily: string | null;
    iconMarkup: string | null;
    fontWeight: number | null;
    lineHeight: number | null;
    background: string | null;
    textAlign: string | null;
    fontSize: number | null;
    parentId: number | null;
    childIds: Array<number>;
    shadow: string | null;
    letterSpacing: number;
    kind: AnatomyPartKind;
    color: string | null;
    borderWidth: number;
    text: string | null;
    rect: AnatomyRect;
    rotation: number;
    radius: number;
    depth: number;
    id: number;
};

export type AnatomyMeasurement = {
    hitRect: AnatomyRect | null;
    parts: Array<AnatomyPart>;
    themeName: DocsThemeName;
    height: number;
    width: number;
};

type AnatomyPartInput = Omit<AnatomyPart, "childIds" | "depth" | "id">;

const MAX_PARTS = 48;

const MEDIA_TAGS = [
    "canvas",
    "video",
    "img"
];

const BORDER_SIDES: Array<keyof AnatomyInsets> = [
    "bottom",
    "right",
    "left",
    "top"
];

const RADIUS_CORNERS = [
    "border-bottom-right-radius",
    "border-bottom-left-radius",
    "border-top-right-radius",
    "border-top-left-radius"
];

const NO_INSETS: AnatomyInsets = {
    bottom: 0,
    right: 0,
    left: 0,
    top: 0
};

const ICON_SHAPES = "path, circle, ellipse, line, polyline, polygon, rect";

const HIT_SELECTOR = [
    "[role=\"switch\"]",
    "[role=\"checkbox\"]",
    "[role=\"radio\"]",
    "[role=\"button\"]",
    "[role=\"slider\"]",
    "[role=\"tab\"]",
    "[role=\"link\"]",
    "[role=\"menuitem\"]",
    "[role=\"option\"]",
    "button",
    "a[href]",
    "input",
    "textarea",
    "select",
    "[tabindex]:not([tabindex=\"-1\"])"
].join(", ");

const EMPTY_TEXT = {
    letterSpacing: 0,
    fontFamily: null,
    fontWeight: null,
    lineHeight: null,
    textAlign: null,
    fontSize: null,
    textLines: [],
    text: null
};

const EMPTY_ICON = {
    iconViewBox: null,
    iconMarkup: null,
    iconStroke: null
};

const round = (value: number) => Math.round(value * 100) / 100;

const parsePx = (value: string) => {
    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed) ? parsed : 0;
};

export const getColorAlpha = (color: string | null) => {
    if(!color || color === "transparent" || color === "none") {
        return 0;
    }

    const match = color.match(/rgba?\(([^)]*)\)/);

    if(!match) {
        return 1;
    }

    const channels = (match[1] ?? "").split(/[\s,/]+/).filter(Boolean);

    return channels.length > 3 ? Number(channels[3]) : 1;
};

const isSameRect = (first: AnatomyRect, second: AnatomyRect) => Math.abs(first.x - second.x) < 0.5 && Math.abs(first.y - second.y) < 0.5 && Math.abs(first.width - second.width) < 0.5 && Math.abs(first.height - second.height) < 0.5;

const getBorder = (style: CSSStyleDeclaration) => {
    let width = 0;

    let color: string | null = null;

    const insets = {
        ...NO_INSETS
    };

    BORDER_SIDES.forEach((side) => {
        const sideStyle = style.getPropertyValue(`border-${side}-style`);

        if(sideStyle === "none" || sideStyle === "hidden") {
            return;
        }

        const sideWidth = parsePx(style.getPropertyValue(`border-${side}-width`));

        const sideColor = style.getPropertyValue(`border-${side}-color`);

        insets[side] = sideWidth;

        if(sideWidth > width && getColorAlpha(sideColor) > 0) {
            width = sideWidth;

            color = sideColor;
        }
    });

    return {
        insets,
        width,
        color
    };
};

const getRadius = (style: CSSStyleDeclaration) => Math.max(...RADIUS_CORNERS.map((corner) => parsePx(style.getPropertyValue(corner))));

const getRadii = (style: CSSStyleDeclaration): AnatomyCornerRadii => [
    parsePx(style.getPropertyValue("border-top-left-radius")),
    parsePx(style.getPropertyValue("border-top-right-radius")),
    parsePx(style.getPropertyValue("border-bottom-right-radius")),
    parsePx(style.getPropertyValue("border-bottom-left-radius"))
];

const getIconColor = (svg: Element) => {
    const shapes = Array.from(svg.querySelectorAll(ICON_SHAPES));

    for(const shape of shapes) {
        const style = window.getComputedStyle(shape);

        if(style.stroke !== "none" && getColorAlpha(style.stroke) > 0 && parsePx(style.strokeWidth) > 0) {
            return style.stroke;
        }

        if(style.fill !== "none" && getColorAlpha(style.fill) > 0) {
            return style.fill;
        }
    }

    return window.getComputedStyle(svg).color;
};

const getIconMarkup = (svg: Element) => {
    const clone = svg.cloneNode(true) as Element;

    for(const node of Array.from(clone.querySelectorAll("*"))) {
        node.removeAttribute("stroke");

        node.removeAttribute("fill");

        if(node instanceof SVGElement) {
            node.style.removeProperty("stroke");

            node.style.removeProperty("fill");
        }
    }

    return clone.innerHTML;
};

const getIconStroke = (svg: Element): AnatomyIconStroke | null => {
    const shapes = Array.from(svg.querySelectorAll(ICON_SHAPES));

    for(const shape of shapes) {
        const style = window.getComputedStyle(shape);

        const width = parsePx(style.strokeWidth);

        if(style.stroke !== "none" && width > 0) {
            return {
                linejoin: style.strokeLinejoin as AnatomyIconStroke["linejoin"],
                linecap: style.strokeLinecap as AnatomyIconStroke["linecap"],
                width
            };
        }
    }

    return null;
};

const getIconViewBox = (svg: Element, rect: AnatomyRect) => {
    const viewBox = svg.getAttribute("viewBox");

    if(viewBox) {
        return viewBox;
    }

    const width = parsePx(svg.getAttribute("width") ?? "") || rect.width;

    const height = parsePx(svg.getAttribute("height") ?? "") || rect.height;

    return `0 0 ${width} ${height}`;
};

const isTextField = (element: Element): element is HTMLInputElement | HTMLTextAreaElement => element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;

const isFormField = (element: Element): element is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement => isTextField(element) || element instanceof HTMLSelectElement;

const getText = (element: Element) => {
    if(element instanceof HTMLSelectElement) {
        return element.selectedOptions[0]?.text ?? null;
    }

    if(isTextField(element)) {
        return element.value || element.placeholder || null;
    }

    const text = (element.textContent ?? "").replace(/\s+/g, " ").trim();

    return text || null;
};

const getTextColor = (element: Element, style: CSSStyleDeclaration) => {
    if(isTextField(element) && !element.value && element.placeholder) {
        return window.getComputedStyle(element, "::placeholder").color;
    }

    return style.color;
};

const getRotation = (element: Element, subject: Element) => {
    let angle = 0;

    let current: Element | null = element;

    while(current && current !== subject) {
        const transform = window.getComputedStyle(current).transform;

        const match = transform.match(/^matrix\(([^)]+)\)$/);

        if(match) {
            const [
                a = 1,
                b = 0
            ] = (match[1] ?? "").split(",").map((value) => Number.parseFloat(value));

            angle += Math.atan2(b, a);
        }

        current = current.parentElement;
    }

    return angle;
};

const unrotateRect = (rect: AnatomyRect, angle: number): AnatomyRect => {
    const cos = Math.abs(Math.cos(angle));

    const sin = Math.abs(Math.sin(angle));

    if(sin < 0.001) {
        return rect;
    }

    const determinant = cos * cos - sin * sin;

    let width = rect.width / (cos + sin);

    let height = rect.height / (cos + sin);

    if(Math.abs(determinant) > 0.1) {
        width = (rect.width * cos - rect.height * sin) / determinant;

        height = (rect.height * cos - rect.width * sin) / determinant;
    }

    return {
        y: round(rect.y + (rect.height - height) / 2),
        x: round(rect.x + (rect.width - width) / 2),
        height: round(height),
        width: round(width)
    };
};

const hasOwnText = (element: Element) => Array.from(element.childNodes).some((node) => node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim().length > 0);

const getTextLines = (element: Element, style: CSSStyleDeclaration, rect: AnatomyRect, toRect: (rect: DOMRect) => AnatomyRect): Array<AnatomyRect> => {
    if(isFormField(element)) {
        const startInset = parsePx(style.paddingLeft) + parsePx(style.borderLeftWidth);

        const endInset = parsePx(style.paddingRight) + parsePx(style.borderRightWidth);

        const fontSize = parsePx(style.fontSize);

        const lineHeight = style.lineHeight === "normal" ? fontSize * 1.2 : parsePx(style.lineHeight);

        return [
            {
                width: Math.max(0, rect.width - startInset - endInset),
                y: round(rect.y + (rect.height - lineHeight) / 2),
                x: round(rect.x + startInset),
                height: lineHeight
            }
        ];
    }

    const range = document.createRange();

    range.selectNodeContents(element);

    const lines: Array<AnatomyRect> = [];

    Array.from(range.getClientRects()).forEach((clientRect) => {
        if(clientRect.width < 0.5 || clientRect.height < 0.5) {
            return;
        }

        const lineRect = toRect(clientRect);

        const line = lines.find((item) => Math.abs(item.y - lineRect.y) < 2);

        if(!line) {
            lines.push(lineRect);

            return;
        }

        const right = Math.max(line.x + line.width, lineRect.x + lineRect.width);

        line.x = Math.min(line.x, lineRect.x);

        line.width = round(right - line.x);

        line.height = Math.max(line.height, lineRect.height);
    });

    range.detach();

    return lines;
};

export const measureAnatomy = (subject: HTMLElement, themeName: DocsThemeName): AnatomyMeasurement => {
    const origin = subject.getBoundingClientRect();

    const parts: Array<AnatomyPart> = [];

    const toRect = (rect: DOMRect): AnatomyRect => ({
        x: round(rect.left - origin.left),
        y: round(rect.top - origin.top),
        height: round(rect.height),
        width: round(rect.width)
    });

    const addPart = (input: AnatomyPartInput) => {
        const parent = input.parentId === null ? null : parts[input.parentId];

        const id = parts.length;

        parts.push({
            ...input,
            depth: parent ? parent.depth + 1 : 0,
            childIds: [],
            id
        });

        parent?.childIds.push(id);

        return id;
    };

    const visit = (element: Element, parentId: number | null) => {
        if(parts.length >= MAX_PARTS) {
            return;
        }

        const style = window.getComputedStyle(element);

        if(style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) {
            return;
        }

        const angle = getRotation(element, subject);

        const rotation = round(angle * 180 / Math.PI);

        const rect = unrotateRect(toRect(element.getBoundingClientRect()), angle);

        const hasBox = rect.width >= 1 && rect.height >= 1;

        const tagName = element.tagName.toLowerCase();

        if(tagName === "svg") {
            if(hasBox) {
                addPart({
                    ...EMPTY_TEXT,
                    iconViewBox: getIconViewBox(element, rect),
                    iconStroke: getIconStroke(element),
                    iconMarkup: getIconMarkup(element),
                    color: getIconColor(element),
                    borderInsets: NO_INSETS,
                    borderColor: null,
                    background: null,
                    borderWidth: 0,
                    kind: "icon",
                    shadow: null,
                    radius: 0,
                    parentId,
                    rotation,
                    rect
                });
            }

            return;
        }

        const border = getBorder(style);

        const radius = getRadius(style);

        const radii = getRadii(style);

        if(MEDIA_TAGS.includes(tagName)) {
            if(hasBox) {
                addPart({
                    ...EMPTY_TEXT,
                    ...EMPTY_ICON,
                    shadow: style.boxShadow === "none" ? null : style.boxShadow,
                    borderInsets: border.insets,
                    borderColor: border.color,
                    borderWidth: border.width,
                    background: null,
                    kind: "media",
                    color: null,
                    parentId,
                    rotation,
                    radius,
                    radii,
                    rect
                });
            }

            return;
        }

        const background = style.backgroundColor;

        const backgroundAlpha = getColorAlpha(background);

        const hasShadow = style.boxShadow !== "none";

        const hasImage = style.backgroundImage !== "none";

        const isOverlay = style.position === "absolute" && border.width === 0 && !hasShadow && !hasImage && backgroundAlpha < 0.3;

        const parent = parentId === null ? null : parts[parentId] ?? null;

        const isDuplicate = parent !== null && parent.kind === "shape" && isSameRect(parent.rect, rect) && parent.background === background && border.width === 0;

        let nextParentId = parentId;

        if(hasBox && !isOverlay && !isDuplicate && (backgroundAlpha > 0 || hasImage || border.width > 0 || hasShadow)) {
            nextParentId = addPart({
                ...EMPTY_TEXT,
                ...EMPTY_ICON,
                background: backgroundAlpha > 0 ? background : null,
                shadow: hasShadow ? style.boxShadow : null,
                borderInsets: border.insets,
                borderColor: border.color,
                borderWidth: border.width,
                kind: "shape",
                color: null,
                parentId,
                rotation,
                radius,
                radii,
                rect
            });
        }

        if(isFormField(element) || hasOwnText(element)) {
            if(hasBox && getColorAlpha(getTextColor(element, style)) > 0) {
                addPart({
                    ...EMPTY_ICON,
                    lineHeight: style.lineHeight === "normal" ? null : round(parsePx(style.lineHeight)),
                    letterSpacing: style.letterSpacing === "normal" ? 0 : parsePx(style.letterSpacing),
                    textLines: getTextLines(element, style, rect, toRect),
                    fontSize: round(parsePx(style.fontSize)),
                    fontWeight: Number(style.fontWeight),
                    color: getTextColor(element, style),
                    fontFamily: style.fontFamily,
                    textAlign: style.textAlign,
                    borderInsets: NO_INSETS,
                    parentId: nextParentId,
                    text: getText(element),
                    borderColor: null,
                    background: null,
                    borderWidth: 0,
                    kind: "text",
                    shadow: null,
                    radius: 0,
                    rotation,
                    rect
                });
            }

            return;
        }

        Array.from(element.children).forEach((child) => visit(child, nextParentId));
    };

    Array.from(subject.children).forEach((child) => visit(child, null));

    const hitElement = subject.querySelector(HIT_SELECTOR);

    return {
        hitRect: hitElement ? toRect(hitElement.getBoundingClientRect()) : null,
        height: round(origin.height),
        width: round(origin.width),
        themeName,
        parts
    };
};

export const getInnerRect = (part: AnatomyPart): AnatomyRect => ({
    height: part.rect.height - part.borderInsets.top - part.borderInsets.bottom,
    width: part.rect.width - part.borderInsets.left - part.borderInsets.right,
    x: part.rect.x + part.borderInsets.left,
    y: part.rect.y + part.borderInsets.top
});

export const getParentInnerRect = (part: AnatomyPart, measurement: AnatomyMeasurement): AnatomyRect => {
    const parent = part.parentId === null ? undefined : measurement.parts[part.parentId];

    return parent ? getInnerRect(parent) : {
        height: measurement.height,
        width: measurement.width,
        x: 0,
        y: 0
    };
};

export const isCircle = (part: AnatomyPart) => part.kind !== "text" && Math.abs(part.rect.width - part.rect.height) < 0.5 && part.radius >= part.rect.width / 2 - 0.01;

export const isSameMeasurement = (first: AnatomyMeasurement | null, second: AnatomyMeasurement) => {
    if(!first) {
        return false;
    }

    return JSON.stringify(first) === JSON.stringify(second);
};
